import { useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import FacebookAccountsList from './AccountsList';
import FacebookAdAccountSettings from './AdAccountSettings';
import { Globe, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';

// "Ad Account" and "Accounts" were two sidebar entries over the same integration:
// one listed every connected ad account, the other configured the default one.
// Same object, two levels of zoom — so they're tabs on one page now, with the
// tab in the URL so links and the back button both work.
type Tab = 'accounts' | 'settings';

const TABS: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
  { id: 'accounts', label: 'Accounts', icon: <Globe size={14} /> },
  { id: 'settings', label: 'Account Settings', icon: <SlidersHorizontal size={14} /> },
];

export default function FacebookAccountsManager() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = raw === 'settings' ? 'settings' : 'accounts';

  const selectTab = (t: Tab) => {
    const p = new URLSearchParams();
    p.set('tab', t);
    setParams(p);
  };

  return (
    <>
      <Topbar
        title="Facebook Ad Accounts"
        breadcrumb="Facebook Ads"
        description="Connect Meta ad accounts and configure the one you're working in."
      />

      <div className="border-b border-border px-7">
        <div className="flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              className={clsx(
                'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'accounts' && <FacebookAccountsList embedded />}
      {tab === 'settings' && <FacebookAdAccountSettings embedded />}
    </>
  );
}
